<template>
  <MainLayout>
    <div class="redemptions-content">
      <v-card elevation="2">
        <v-card-title class="d-flex align-center">
          <v-icon icon="mdi-history" class="mr-2" />
          My Redemptions
        </v-card-title>
        <v-card-text>
          <div v-if="loading" class="d-flex justify-center align-center py-4">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <v-alert
            v-else-if="error"
            type="error"
            class="my-4"
          >
            {{ error }}
          </v-alert>
          <div v-else>
            <v-table v-if="redemptions.length > 0">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Points</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="redemption in redemptions" :key="redemption.id">
                  <td>{{ redemption.itemName }}</td>
                  <td>
                    <v-chip
                      color="primary"
                      variant="flat"
                      size="small"
                      class="font-weight-medium"
                    >
                      <template v-slot:prepend>
                        <span class="mr-1">🙏</span>
                      </template>
                      {{ redemption.points }}
                    </v-chip>
                  </td>
                  <td>{{ new Date(redemption.timestamp).toLocaleString() }}</td>
                  <td>
                    <v-chip
                      :color="redemption.claimed ? 'success' : 'warning'"
                      variant="flat"
                      size="small"
                      class="font-weight-medium"
                    >
                      <template v-slot:prepend>
                        <v-icon
                          :icon="redemption.claimed ? 'mdi-check-circle' : 'mdi-clock'"
                          size="16"
                          color="white"
                        />
                      </template>
                      {{ redemption.claimed ? 'Claimed' : 'Pending' }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </v-table>
            <div v-else class="text-center pa-4">
              <v-icon icon="mdi-history-off" size="48" color="grey" class="mb-2"></v-icon>
              <div class="text-grey">No redemptions yet</div>
              <v-btn
                color="primary"
                variant="text"
                class="mt-4"
                to="/rewards"
              >
                Browse Rewards Store
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import MainLayout from './components/MainLayout.vue'

interface Redemption {
  id: string
  itemName: string
  points: number
  timestamp: number
  claimed: boolean
  claimedAt?: number
}

const redemptions = ref<Redemption[]>([])
const loading = ref(true)
const error = ref('')

async function fetchRedemptions() {
  loading.value = true
  error.value = ''
  
  try {
    console.log('Fetching redemptions...')
    console.log('API URL:', `${process.env.VUE_APP_API_BASE_URL}/my-redemptions`)
    
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/my-redemptions`, {
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
      }
    })
    
    console.log('Response status:', response.status)
    console.log('Response headers:', Object.fromEntries(response.headers.entries()))
    
    if (!response.ok) {
      const errorText = await response.text()
      console.error('Error response:', errorText)
      throw new Error(`Failed to fetch redemptions: ${response.status} ${response.statusText}`)
    }
    
    const data = await response.json()
    console.log('Redemptions data:', data)
    
    if (!Array.isArray(data)) {
      console.error('Unexpected data format:', data)
      throw new Error('Received invalid data format from server')
    }
    
    redemptions.value = data
  } catch (e) {
    console.error('Error details:', e)
    error.value = `Failed to load redemptions: ${e.message}`
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  console.log('Component mounted, fetching redemptions...')
  fetchRedemptions()
})
</script>

<style scoped>
.redemptions-content {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}
</style> 